/**
 * Chart Instance Hook for ProcessedDataChart
 * Manages uPlot chart lifecycle and configuration
 */

import { useEffect, useRef, useCallback } from 'react';
import uPlot from 'uplot';
import { ProcessedChartData, DisplayData } from '../types';
import { CHART_HEIGHT } from '../constants';
import { formatAxisTime, formatNumber } from '../utils';
import {
  wheelZoomPlugin,
  touchZoomPlugin,
  dragSelectPlugin,
  syncScalesPlugin,
  adaptivePointsPlugin
} from '../plugins';

interface UseChartInstanceProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  displayData: DisplayData;
  processedData: ProcessedChartData;
  seriesName: string;
  sharedBrushRange?: { startTime: number; endTime: number } | null;
  onSharedBrushChange?: (range: { startTime: number; endTime: number } | null) => void;
  showPointsRef?: React.MutableRefObject<boolean>;
  showPointsEnabledRef?: React.MutableRefObject<boolean>;
}

export function useChartInstance({
  containerRef,
  displayData,
  processedData,
  seriesName,
  sharedBrushRange,
  onSharedBrushChange,
  showPointsRef,
  showPointsEnabledRef
}: UseChartInstanceProps) {
  const chartRef = useRef<uPlot | null>(null);
  const isInternalUpdate = useRef(false);
  const processedDataRef = useRef(processedData);
  processedDataRef.current = processedData;
  // Preserve zoom state across chart re-creations (e.g., when showAll toggles)
  const savedScaleRef = useRef<{ xMin: number; xMax: number } | null>(null);

  // Reset zoom function
  const handleResetZoom = useCallback(() => {
    if (chartRef.current && processedDataRef.current.timestamps.length > 0) {
      const { timestamps } = processedDataRef.current;

      // Clear saved scale so next chart creation uses full range
      savedScaleRef.current = null;

      isInternalUpdate.current = true;
      chartRef.current.setScale("x", {
        min: timestamps[0],
        max: timestamps[timestamps.length - 1],
      });

      // Notify parent about reset
      if (onSharedBrushChange) {
        onSharedBrushChange({
          startTime: timestamps[0] * 1000,
          endTime: timestamps[timestamps.length - 1] * 1000,
        });
      }

      setTimeout(() => {
        isInternalUpdate.current = false;
      }, 50);
    }
  }, [onSharedBrushChange]);

  // Handle external sync - when sharedBrushRange changes from parent
  useEffect(() => {
    if (chartRef.current && sharedBrushRange && !isInternalUpdate.current) {
      isInternalUpdate.current = true;
      chartRef.current.setScale("x", {
        min: sharedBrushRange.startTime / 1000, // ms → seconds
        max: sharedBrushRange.endTime / 1000,
      });
      setTimeout(() => {
        isInternalUpdate.current = false;
      }, 50);
    }
  }, [sharedBrushRange]);

  // Create/update chart
  useEffect(() => {
    if (!containerRef.current || displayData.timestamps.length === 0) return;

    const { timestamps, values } = displayData;
    const container = containerRef.current;

    // Save current zoom state before destroying chart
    if (chartRef.current) {
      const u = chartRef.current;
      if (u.scales.x.min !== undefined && u.scales.x.max !== undefined) {
        savedScaleRef.current = {
          xMin: u.scales.x.min,
          xMax: u.scales.x.max
        };
      }
      chartRef.current.destroy();
      chartRef.current = null;
    }

    // Clear container
    container.innerHTML = "";

    // Calculate Y domain with padding
    const { yMin, yMax } = processedData;
    const yPadding = (yMax - yMin) * 0.05 || 0.1;
    const calcYMin = yMin - yPadding;
    const calcYMax = yMax + yPadding;

    // Ensure container has dimensions before creating chart
    if (container.clientWidth === 0) {
      const rafId = requestAnimationFrame(() => {
        if (containerRef.current && containerRef.current.clientWidth > 0) {
          container.innerHTML = "";
        }
      });
      return () => cancelAnimationFrame(rafId);
    }

    // Create plugins
    const plugins: uPlot.Plugin[] = [
      wheelZoomPlugin({ factor: 0.75 }),
      touchZoomPlugin(),
      dragSelectPlugin(),
    ];

    // Add sync plugin if callback provided
    if (onSharedBrushChange) {
      plugins.push(syncScalesPlugin(onSharedBrushChange, isInternalUpdate));
    }

    // Add adaptive points plugin if refs provided
    if (showPointsRef && showPointsEnabledRef) {
      plugins.push(adaptivePointsPlugin(showPointsRef, showPointsEnabledRef));
    }

    const opts: uPlot.Options = {
      width: container.clientWidth,
      height: CHART_HEIGHT,
      plugins,
      cursor: {
        drag: {
          x: true,
          y: false, // X-axis only zoom
          uni: 50,
        },
      },
      scales: {
        x: {
          time: true,
        },
        y: {
          range: (_u, dataMin, dataMax) => {
            // Handle edge cases - uPlot passes Infinity when all values are null
            if (!Number.isFinite(dataMin) || !Number.isFinite(dataMax)) {
              return [calcYMin, calcYMax];
            }
            if (dataMin === dataMax) {
              return [dataMin - 1, dataMax + 1];
            }
            const padding = (dataMax - dataMin) * 0.05;
            return [dataMin - padding, dataMax + padding];
          },
        },
      },
      axes: [
        {
          stroke: "#64748b",
          grid: { stroke: "#e2e8f0", width: 1 },
          ticks: { stroke: "#94a3b8", width: 1 },
          values: (_u, splits) => splits.map((v) => formatAxisTime(v)),
          rotate: -15,
          gap: 5,
        },
        {
          stroke: "#64748b",
          grid: { stroke: "#e2e8f0", width: 1 },
          ticks: { stroke: "#94a3b8", width: 1 },
          values: (_u, splits) => splits.map((v) => formatNumber(v)),
          label: seriesName,
          labelSize: 20,
          labelGap: 8,
        },
      ],
      series: [
        {
          value: (_u: uPlot, v: number | null) => v == null ? "--" : formatAxisTime(v),
        },
        {
          label: seriesName,
          stroke: "#14B8A6",
          width: 1.5,
          fill: "#14B8A620",
          points: showPointsRef
            ? {
                show: () => showPointsRef.current,
                size: 4,
                fill: "#14B8A6",
                stroke: "#14B8A6",
              }
            : { show: false },
        },
      ],
    };

    const uplotData: uPlot.AlignedData = [timestamps, values];
    chartRef.current = new uPlot(opts, uplotData, container);

    // Restore zoom state: prioritize sharedBrushRange, then savedScale
    if (chartRef.current) {
      isInternalUpdate.current = true;
      if (sharedBrushRange) {
        // Apply shared range from parent
        chartRef.current.setScale("x", {
          min: sharedBrushRange.startTime / 1000,
          max: sharedBrushRange.endTime / 1000,
        });
      } else if (savedScaleRef.current) {
        // Restore previously saved zoom state
        chartRef.current.setScale("x", {
          min: savedScaleRef.current.xMin,
          max: savedScaleRef.current.xMax,
        });
      }
      setTimeout(() => {
        isInternalUpdate.current = false;
      }, 50);
    }

    // Handle resize
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (chartRef.current) {
          chartRef.current.setSize({
            width: entry.contentRect.width,
            height: CHART_HEIGHT,
          });
        }
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayData, seriesName, processedData.yMin, processedData.yMax, onSharedBrushChange]);

  return { chartRef, handleResetZoom };
}
